import React, { createContext, useContext, useState, useMemo } from "react";

const BoothContext = createContext();

export function BoothProvider({ booth, children }) {
	const [sessionState, setSessionState] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	// Booth-derived data and capabilities
	const boothCapabilities = useMemo(() => {
		if (!booth) return {};

		return {
			// Booking capabilities based on booth type
			canBookNow: booth.type === " paid-byMinute",
			canSchedule: booth.type === "reservation",
			canUnlock: sessionState?.status === "new",

			// Display information
			costDisplay:
				booth.type === " paid-byMinute"
					? `$${booth.costPerMinute}/min`
					: "Per reservation",

			// Booth configuration
			isWalkUp: booth.type === " paid-byMinute",
			isReservation: booth.type === "reservation",

			// Location and access info
			locationInfo: booth.location
				? {
						name: booth.location.name,
						city: booth.location.city,
						state: booth.location.state,
						timezone: booth.location.timezone || "America/New_York",
						saltoLocationId: booth.location.saltoLocationId,
				  }
				: null,

			// Lock and access
			lockId: booth.lockId,
			boothId: booth.boothId, // Numeric ID
			boothObjectId: booth._id, // MongoDB ObjectId
		};
	}, [booth, sessionState]);

	// Session management functions
	const updateSessionState = (newState) => {
		setSessionState((prevState) => ({
			...prevState,
			...newState,
		}));
	};

	const setLoadingState = (isLoading) => {
		setLoading(isLoading);
	};

	const setErrorState = (errorMessage) => {
		setError(errorMessage);
	};

	const clearError = () => {
		setError(null);
	};

	const contextValue = {
		// Booth data
		booth,
		boothCapabilities,

		// Session state
		sessionState,
		updateSessionState,

		// UI state
		loading,
		setLoadingState,
		error,
		setErrorState,
		clearError,

		// Computed state
		isBoothAvailable: booth?.status === "available" && !booth?.isOccupied,
		boothDisplayName: booth
			? `${booth.location?.name} - Booth ${booth.boothId}`
			: "",
	};

	return (
		<BoothContext.Provider value={contextValue}>
			{children}
		</BoothContext.Provider>
	);
}

export function useBooth() {
	const context = useContext(BoothContext);
	if (!context) {
		throw new Error("useBooth must be used within a BoothProvider");
	}
	return context;
}

export default BoothContext;
